import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';
import { useMutation, useLazyQuery } from '@apollo/client';
import cn from 'classnames';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import router from 'next/router';
// Api
import { CREATE_STREAM } from 'api/streams/mutations';
import { StreamFields } from 'api/streams/fragments';
import { ESTIMATE_STREAM_PRICE } from 'api/streams/queries';
// Types
import {
  CreateStream,
  CreateStreamVariables,
} from 'api/streams/types/CreateStream';
import { TimeZoneOption } from 'constants/timeZone';
import { MyStreams_myStreams_entities } from 'api/streams/types/MyStreams';
import {
  EstimateStreamPrice,
  EstimateStreamPriceVariables,
} from 'api/streams/types/EstimateStreamPrice';
// Hooks
import { useGetCurrUser } from 'hooks';
import { useGetPpvTiers } from 'hooks/useGetPpvTiers';
// Helpers
import {
  getTimeZoneObject,
  getCurrentTimeZoneOffset,
  getDateWithoutCurrentTimeZone,
  getCurrentTimeZone,
  toLegacyScheduleDateString,
} from 'helpers/timeZone';
import { computeStartStreamPath } from 'helpers/routes';
import { buildGoLiveStreamSeoCopy, formatHashtagInput } from 'helpers/streams';
// Ui
import Switch from 'ui3/Switch/Switch';
import Button from 'ui3/Button/Button';
import Text from 'ui3/Text/Text';
import TextField from 'ui3/TextField/TextField';
// Icons
import Icon from 'ui/Icon/Icon';
import Input from 'ui3/Input/Input';
import TagsInput from 'ui3/TagsInput/TagsInput';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Components
import { showToast } from 'components/common/Toast/Toast';
import StreamCoverImage from './StreamCoverImage/StreamCoverImage';
import GoLiveOptionsModal from './GoLiveOptionsModal/GoLiveOptionsModal';
import EasyImageCrop from 'components/common/EasyImageCrop/EasyImageCrop';
import PriceTierSelector from 'components/common3/PriceTierSelector/PriceTierSelector';
import {
  MatchupsModal,
  FightDraft,
  EMPTY_FIGHT_DRAFT,
  draftToMatchupInput,
} from 'components/StartStream/components/FightCard';
import { CreateMatchupInput } from 'api/graphql-global-types';
// Styles
import styles from './CreateEditGoLiveStream.module.scss';

dayjs.extend(utc);
dayjs.extend(timezone);

type CreateEditStreamProps = {
  stream?: MyStreams_myStreams_entities;
  onComplete?: () => void;
  isEvent?: boolean;
};

type FormInputs = {
  name: string;
  description: string;
  scheduleDate: Date | string;
  timeZone: TimeZoneOption;
  price: string;
  isGoLiveFree: boolean;
  isGoLiveHidden: boolean;
  image: File | null;
  previewImage: string;
  hashtags?: string[];
};

const getValidationSchema = () => {
  return yup.object().shape({
    name: yup
      .string()
      .max(100, 'Max 100 characters')
      .required('Title is required'),
    description: yup
      .string()
      .max(500, 'Max 500 characters')
      .required('Description is required'),
    timeZone: yup
      .object()
      .shape({
        label: yup.string(),
        tzCode: yup.string(),
        offset: yup.number(),
      })
      .typeError('Time Zone is required')
      .required('Time Zone is required'),
    isGoLiveFree: yup.boolean(),
    isGoLiveHidden: yup.boolean(),
    price: yup.string().when(['isGoLiveFree'], {
      is: (isFree) => !isFree,
      then: yup
        .string()
        .required('Price is required')
        .matches(
          /^(?:0\.\d{0,2}[1-9]|(?!0)\d{1,}(?:\.\d{0,2}[0-9])?)$/,
          'Only numbers are allowed'
        ),
      otherwise: yup.string(),
    }),
    previewImage: yup.string().required(),
    image: yup.mixed(),
  });
};

const CreateEditGoLiveStream = ({
  stream,
  onComplete,
  isEvent = false,
}: CreateEditStreamProps) => {
  const defaultTimeZone: TimeZoneOption = getTimeZoneObject(stream);
  const { data: userData } = useGetCurrUser();
  const streamDate = getDateWithoutCurrentTimeZone(stream?.scheduleDate);

  const [profit, setProfit] = useState<number>(0);
  const [estimateStreamPrice] = useLazyQuery<
    EstimateStreamPrice,
    EstimateStreamPriceVariables
  >(ESTIMATE_STREAM_PRICE);
  const [optionsStepOpen, setOptionsStepOpen] = useState(false);
  const [matchupsStepOpen, setMatchupsStepOpen] = useState(false);
  const [fightDrafts, setFightDrafts] = useState<FightDraft[]>([]);
  const [fanPollingEnabled, setFanPollingEnabled] = useState(false);
  const [sharePickGraphicsEnabled, setSharePickGraphicsEnabled] = useState(
    false
  );
  const pendingValuesRef = useRef<FormInputs | null>(null);

  const {
    register,
    handleSubmit,
    errors,
    setValue,
    unregister,
    watch,
    control,
  } = useForm<FormInputs>({
    resolver: yupResolver(getValidationSchema()),
    mode: 'onChange',
    defaultValues: {
      name: '',
      description: '',
      scheduleDate:
        stream?.scheduleDate && dayjs(stream.scheduleDate).isValid()
          ? dayjs(streamDate).toDate()
          : '',
      timeZone: defaultTimeZone,
      price: stream?.price ? `${stream.price}` : '',
      isGoLiveFree: isEvent ? false : Boolean(stream?.isFree),
      isGoLiveHidden: Boolean(stream?.isHidden),
      previewImage: '',
      hashtags: stream?.hashtags?.map((tag) => tag.name) || [],
    },
  });

  useEffect(() => {
    if (userData) {
      const { name, description } = buildGoLiveStreamSeoCopy({
        storeName: userData?.me?.storeDetails?.storeName,
        sport: userData?.me.sports?.[0]?.name,
      });

      setValue('name', name);
      setValue('description', description);
      setValue('previewImage', userData?.me.storeDetails?.avatarURL || '');
    }
  }, [setValue, userData]);

  const [createStream, { loading: createStreamLoading }] = useMutation<
    CreateStream,
    CreateStreamVariables
  >(CREATE_STREAM, {
    update(cache, { data }) {
      cache.modify({
        fields: {
          myStreams(existingStreams = {}) {
            const newStreamRef = cache.writeFragment({
              data: data?.createStream,
              fragment: StreamFields,
            });

            return {
              ...existingStreams,
              entities: [newStreamRef, ...existingStreams.entities],
            };
          },
        },
      });
    },
  });

  useEffect(() => {
    register('image');
    register('previewImage');

    return () => {
      unregister('image');
      unregister('previewImage');
    };
  }, [register, unregister]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setValue('image', acceptedFiles[0], { shouldValidate: true });
    },
    [setValue]
  );

  const handleApplyImage = useCallback(
    (image: File) => {
      setValue('previewImage', URL.createObjectURL(image), {
        shouldValidate: true,
      });
      setValue('image', image, { shouldValidate: true });
    },
    [setValue]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: 'image/jpg, image/jpeg, image/png',
    maxSize: 10 * 1024 * 1024,
    onDropRejected: () => {
      showToast({
        message: 'Please upload a JPG or PNG image up to 10MB.',
        type: 'error',
      });
    },
  });

  const submitCreate = async (
    values: FormInputs,
    matchups: CreateMatchupInput[] = [],
    engagement: {
      fanPollingEnabled: boolean;
      sharePickGraphicsEnabled: boolean;
    } = { fanPollingEnabled: false, sharePickGraphicsEnabled: false }
  ): Promise<void> => {
    const timeZoneOffset = dayjs
      .tz(
        dayjs(values.scheduleDate).format('YYYY-MM-DD HH:mm'),
        values.timeZone.tzName
      )
      .utcOffset();

    const sponsorsInput =
      stream?.sponsors?.length === 0 || !stream?.sponsors
        ? []
        : [
            {
              name: (stream?.sponsors && stream?.sponsors[0]?.name) || '',
              logo: stream?.sponsors && stream?.sponsors[0]?.logoUrl,
              id: stream?.sponsors && stream?.sponsors[0]?.id,
            },
          ];

    let imageToSend: File | null = values.image ?? null;
    if (!imageToSend && values.previewImage) {
      try {
        const response = await fetch(values.previewImage);
        const blob = await response.blob();
        const ext = blob.type?.split('/')[1] || 'jpeg';
        imageToSend = new File([blob], `stream-cover.${ext}`, {
          type: blob.type || 'image/jpeg',
        });
      } catch {
        imageToSend = null;
      }
    }

    const variables = {
      sponsors: sponsorsInput,
      moderatorId: stream?.moderator?.id,
      name: values.name,
      description: values.description,
      hashtagInputs: formatHashtagInput(values.hashtags),
      ...(imageToSend
        ? {
            image: imageToSend,
          }
        : {}),
      scheduleDate: toLegacyScheduleDateString(values.scheduleDate),
      timeZone: {
        tzCode: values.timeZone.tzCode,
        offset: timeZoneOffset,
      },
    };

    try {
      {
        const { data } = await createStream({
          variables: {
            input: {
              isFree: values.isGoLiveFree,
              isPPV: !values.isGoLiveFree,
              isHidden: values.isGoLiveHidden,
              ...(!values.isGoLiveFree && {
                price: Number(values.price),
              }),
              ...(matchups.length && { matchups }),
              fanPollingEnabled: engagement.fanPollingEnabled,
              sharePickGraphicsEnabled: engagement.sharePickGraphicsEnabled,
              postEventContentHubEnabled: !values.isGoLiveFree,
              ...variables,
              timeZone: {
                tzCode: getCurrentTimeZone().tzCode,
                offset: getCurrentTimeZoneOffset(),
              },
              sponsors: [],
            },
          },
        });
        const startStreamPath = computeStartStreamPath(data?.createStream);
        router.push(startStreamPath);
      }

      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.log('error', error);
      showToast({
        message: `Something went wrong. (${error?.message})`,
        type: 'error',
      });
    }
  };

  const handleFormSubmit: SubmitHandler<FormInputs> = async (
    values
  ): Promise<void> => {
    if (values.isGoLiveFree) {
      await submitCreate(values);
      return;
    }
    pendingValuesRef.current = values;
    setOptionsStepOpen(true);
  };

  const isFilledDraft = (draft: FightDraft) =>
    Boolean(draft.fighterA.name.trim() || draft.fighterB.name.trim());

  const handleMatchupsSave = (drafts: FightDraft[]) => {
    const hasBouts = drafts.some(isFilledDraft);
    setFightDrafts(drafts);
    setFanPollingEnabled(hasBouts);
    setSharePickGraphicsEnabled(hasBouts);
    setMatchupsStepOpen(false);
    setOptionsStepOpen(true);
  };

  const handleMatchupsClose = () => {
    setMatchupsStepOpen(false);
    setOptionsStepOpen(true);
  };

  const handleEditMatchCard = () => {
    setOptionsStepOpen(false);
    setMatchupsStepOpen(true);
  };

  const handleOptionsSubmit = async () => {
    const values = pendingValuesRef.current;
    if (!values) {
      return;
    }
    await submitCreate(values, fightDrafts.map(draftToMatchupInput), {
      fanPollingEnabled,
      sharePickGraphicsEnabled,
    });
  };

  const filledDrafts = fightDrafts.filter(isFilledDraft);
  const mainEventDraft = filledDrafts.find((draft) => draft.isMainEvent);
  const matchCardSummary = filledDrafts.length
    ? [
        `${filledDrafts.length} ${
          filledDrafts.length === 1 ? 'bout' : 'bouts'
        }`,
        mainEventDraft &&
          `Main event: ${mainEventDraft.fighterA.name.trim() || 'TBD'} vs ${
            mainEventDraft.fighterB.name.trim() || 'TBD'
          }`,
      ]
        .filter(Boolean)
        .join(' · ')
    : null;

  const handlePreviewImageRemove = () => {
    setValue('previewImage', '', {
      shouldValidate: true,
    });
    setValue('image', null, { shouldValidate: true });
  };

  const image = watch('image');
  const previewImage = watch('previewImage');
  const isFreeStream = watch('isGoLiveFree');
  const price = watch('price');
  const {
    tiers: ppvTiers,
    loading: ppvTiersLoading,
    error: ppvTiersError,
    refetch: refetchPpvTiers,
  } = useGetPpvTiers();

  useEffect(() => {
    if (price) {
      estimateStreamPrice({
        variables: { input: { finalPrice: Number(price) } },
      }).then(({ data }) => {
        if (data?.estimateStreamPrice) {
          setProfit(data.estimateStreamPrice.profit);
        }
      });
    }
  }, [price, estimateStreamPrice]);

  const renderStreamImage = () => {
    if (image) {
      return (
        <EasyImageCrop
          className={styles.imageCropper}
          imageFile={image}
          onApplyImage={handleApplyImage}
          aspect={1}
        />
      );
    }

    return (
      <StreamCoverImage
        previewImage={previewImage}
        fullname={userData?.me?.storeDetails?.storeName || ''}
        onSetNewImage={handleApplyImage}
      />
    );
  };

  return (
    <form
      className={styles.form}
      onSubmit={handleSubmit(handleFormSubmit)}
      autoComplete="off"
      aria-label="stream details"
    >
      <div className={styles.columns}>
        {/* ---- LEFT: details ---- */}
        <div className={styles.details}>
          <Text variant="body1Medium18" color="lights-high">
            Details
          </Text>

          <TextField
            name="name"
            label="Title (required)"
            labelOutside
            placeholder="Add Title"
            notice="Keep titles 40–65 chars for best SEO."
            maxLength={100}
            ref={register}
            error={errors?.name?.message}
            className={styles.input}
          />

          <TextField
            type="textarea"
            name="description"
            label="Description (required)"
            labelOutside
            placeholder="Add description"
            notice="Aim for 120–300 chars with primary keywords up front."
            maxLength={500}
            rows={4}
            ref={register}
            error={errors?.description?.message}
            className={styles.textarea}
          />

          <Controller
            name="hashtags"
            control={control}
            defaultValue={[]}
            render={({ onChange, value: tags }) => (
              <TagsInput
                tags={tags || []}
                label="Tags"
                onChange={onChange}
                name="hashtags"
              />
            )}
          />

          <Text variant="body1Medium18" color="lights-high">
            Pricing
          </Text>
          <div className={styles.inputWithSwitchWrapper}>
            <div className={styles.priceInputContainer}>
              {stream ? (
                <Input
                  name="price"
                  aria-label="Stream price"
                  placeholder="1 USD"
                  icon="dollar"
                  ref={register}
                  error={isFreeStream ? '' : errors?.price?.message}
                  readOnly
                  disabled
                />
              ) : (
                <>
                  <Controller
                    control={control}
                    name="price"
                    defaultValue=""
                    render={({ onChange, value }) => (
                      <PriceTierSelector
                        tiers={ppvTiers}
                        value={value ?? ''}
                        onSelect={(selected) => onChange(String(selected))}
                        loading={ppvTiersLoading}
                        error={ppvTiersError}
                        onRetry={() => refetchPpvTiers()}
                        disabled={isFreeStream}
                      />
                    )}
                  />
                  {!isFreeStream && errors?.price?.message && (
                    <Text variant="captionRegular" color="error-default">
                      {errors.price.message}
                    </Text>
                  )}
                </>
              )}
              <div className={styles.priceNotice}>
                <MillionsIcon name="infoCircle" size={16} />
                <Text variant="captionRegular" color="lights-medium">
                  You earn{' '}
                  <span
                    className={cn({
                      [styles.priceNoticeAmount]: Number(price) > 0,
                    })}
                  >
                    {profit} USD
                  </span>
                </Text>
              </div>
            </div>
            <Switch
              wrapperClassName={styles.switch}
              label="Free Stream"
              name="isGoLiveFree"
              ref={register}
              disabled={!!stream}
              readOnly={!!stream}
            />
          </div>

          {!stream && (
            <div className={styles.testSection}>
              <div className={styles.testSwitchWrapper}>
                <Text variant="body1Medium18" color="lights-high">
                  Test Stream
                </Text>
                <Switch
                  wrapperClassName={styles.switch}
                  name="isGoLiveHidden"
                  aria-label="Test stream"
                  ref={register}
                  disabled={!!stream}
                  readOnly={!!stream}
                />
              </div>
              <Text variant="captionRegular" color="lights-medium">
                Test streams are not visible on the platform.
              </Text>
            </div>
          )}
        </div>

        {/* ---- RIGHT: thumbnail ---- */}
        <div className={styles.thumbnail}>
          <Text variant="body1Medium18" color="lights-high">
            Thumbnail
          </Text>
          <Text variant="captionRegular" color="lights-medium">
            Select or upload a picture that represents your stream and draws
            viewers&rsquo; attention.
          </Text>

          {image || previewImage ? (
            <div className={styles.previewImageWrapper}>
              {renderStreamImage()}
              <button
                type="button"
                className={styles.removePreviewButton}
                onClick={handlePreviewImageRemove}
                aria-label="Delete stream poster"
              >
                <Icon name="trash" className={styles.removePreviewIcon} />
              </button>
            </div>
          ) : (
            <div
              {...getRootProps()}
              className={cn(styles.dropzone, {
                [styles.error]: errors.previewImage,
              })}
            >
              <input {...getInputProps()} aria-label="upload stream image" />
              <div className={styles.dragArea}>
                <Icon name="upload" className={styles.dragAreaIcon} />
                <p className={styles.dragAreaTitle}>Upload Thumbnail</p>
                <p className={styles.dragAreaText}>
                  Upload a JPG or PNG image, max. 10MB, in 1:1 ratio
                </p>
              </div>
            </div>
          )}

          <div className={styles.submitRow}>
            <Button
              className={styles.submitButton}
              type="submit"
              loading={createStreamLoading}
            >
              {/* PAID stream → the Stream Options step follows; FREE → creates directly. */}
              {isFreeStream ? 'Set Up Stream' : 'Next'}
            </Button>
          </div>
        </div>
      </div>

      <GoLiveOptionsModal
        open={optionsStepOpen}
        onClose={() => setOptionsStepOpen(false)}
        matchCardSummary={matchCardSummary}
        hasMatchCard={filledDrafts.length > 0}
        onEditMatchCard={handleEditMatchCard}
        fanPollingEnabled={fanPollingEnabled}
        onFanPollingChange={setFanPollingEnabled}
        sharePickGraphicsEnabled={sharePickGraphicsEnabled}
        onSharePickGraphicsChange={setSharePickGraphicsEnabled}
        onSubmit={handleOptionsSubmit}
        submitting={createStreamLoading}
      />
      <MatchupsModal
        open={matchupsStepOpen}
        onClose={handleMatchupsClose}
        drafts={fightDrafts.length ? fightDrafts : [{ ...EMPTY_FIGHT_DRAFT }]}
        onSave={handleMatchupsSave}
      />
    </form>
  );
};

export default CreateEditGoLiveStream;
